import Modal from "../../Unknown/Modal";
import { useAppDispatch, useAppSelector } from "../../../modules/store/hooks";
import { modalsSlice } from "../../../modules/store/reducers/modalsSlice";
import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import translate from "../../../i18n/translate";
import { useIntl } from "react-intl";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { useState } from "react";
import { organizerApi } from "../../../modules/services/organizerService";
import toast from "react-hot-toast";

export default function CancelTicketByUuidModal() {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { toggleCancelTicketByUuidModal } = modalsSlice.actions;
  const visible = useAppSelector((state) => state.modalsReducer.cancelTicketByUuid);

  const [view, setView] = useState<'search' | 'item'>('search');
  const [sessionId, setSessionId] = useState<string>('');
  const [guestDetails, setGuestDetails] = useState<any>({
    count: 0,
    list: [],
  });
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [changeStatus, setChangeStatus] = useState<boolean>(false);
  const [returnMoney, setReturnMoney] = useState<boolean>(false);
  const [sendSms, setSendSms] = useState<boolean>(false);
  const [generateDocs, setGenerateDocs] = useState<boolean>(false);
  const [fullReturn, setFullReturn] = useState<boolean>(false);
  const [existDocumentSkip, setExistDocumentSkip] = useState<boolean>(false);
  const [isConfirmOverdraftCancel, setIsConfirmOverdraftCancel] = useState<boolean>(false);

  const [cancelTicketByUuid, { isLoading }] = organizerApi.useCancelTicketByUuidMutation();

  const onCloseModal = () => {
    setView('search');
    setSessionId('');
    setGuestDetails({ count: 0, list: [] });
    setIsFetching(false);
    setChangeStatus(false);
    setReturnMoney(false);
    setSendSms(false);
    setGenerateDocs(false);
    setFullReturn(false);
    setExistDocumentSkip(false);
    setIsConfirmOverdraftCancel(false);
    dispatch(toggleCancelTicketByUuidModal());
  }

  const handleContinue = async () => {
    const trimmed = sessionId.trim();
    if (!trimmed) return;
    try {
      setIsFetching(true);
      const response: any = await dispatch(organizerApi.endpoints.getGuestDetails.initiate({ sessionId: trimmed }));

      if (!response?.data?.list?.length) {
        toast.error(response?.error?.data?.message || 'error');
        return;
      }

      setGuestDetails(response.data);
      setChangeStatus(false);
      setReturnMoney(false);
      setSendSms(false);
      setGenerateDocs(false);
      setFullReturn(false);
      setIsConfirmOverdraftCancel(false);
      setView('item');
    } catch (error: any) {
      toast.error(error?.message);
    } finally {
      setIsFetching(false);
    }
  }

  const handleBack = () => {
    setGuestDetails({ count: 0, list: [] });
    setView('search');
  }

  const handleSubmit = async () => {
    try {
      const res = await cancelTicketByUuid({
        sessionId: sessionId.trim(),
        changeStatus,
        returnMoney,
        sendSms,
        yeshInvoiceDocs: generateDocs,
        fullReturn,
        existDocumentSkip,
      }) as any;

      if (res?.data?.message || res?.data?.status === 400 || res?.error) {
        toast.error(res?.data?.message || res?.error?.data?.message || 'error');
      } else {
        toast.success(translate("ticketChangedSuccess"));
        onCloseModal();
      }
    } catch (error: any) {
      toast.error(error?.message);
    }
  }

  const searchView = () => (
    <Box my={2} p={2} display="flex" flexDirection="column" justifyContent="center" width={1} alignItems="center" gap={2}>
      <TextField
        variant='outlined'
        disabled={isFetching}
        fullWidth
        value={sessionId}
        onChange={(e) => setSessionId(e.target.value)}
        placeholder={intl.formatMessage({ id: "sessionIdPlaceholder" })}
        dir="ltr"
        sx={{
          '.MuiInputBase-root.MuiInput-root:before': { borderBottom: "0px" },
          '.MuiInputBase-root.MuiInput-root:hover': { borderBottom: "0px" },
        }}
      />
      <Button
        size="large"
        variant="contained"
        color="warning"
        fullWidth
        onClick={handleContinue}
        disabled={!sessionId.trim() || isFetching}
      >
        {translate("continue")}
      </Button>
    </Box>
  );

  const itemView = () => (
    <Box my={2} p={2} display="flex" flexDirection="column" justifyContent="center" width={1} alignItems="center" gap={1}>
      <Box display="flex" flexDirection="column" gap={1} width={1}>
        <Box
          border='1px solid #ed6c02'
          borderRadius={2}
          p={1}
          width={1}
          display="flex"
          flexDirection="column"
          gap={0.25}
        >
          <Typography variant='body2' align="center">{translate("buyerUser")}</Typography>
          <Typography variant='body2'>{guestDetails?.list?.[0]?.buyerUser?.name} {guestDetails?.list?.[0]?.buyerUser?.surname}</Typography>
          <Typography variant='body2'>{guestDetails?.list?.[0]?.buyerUser?.phone}</Typography>
        </Box>
        {guestDetails?.list?.map((item: any) => (
          <Box
            border='1px solid #ed6c02'
            borderRadius={2}
            p={1}
            width={1}
            key={item.id}
            display="flex"
            flexDirection="column"
            gap={0.25}
          >
            <Box display='flex' gap={0.5} width={1}>
              <Box width={1} display="flex" flexDirection="column" justifyContent="center">
                <Typography variant='body2' align="center">{translate("ticketUser")}</Typography>
                <Typography variant='body2'>{item?.guestUser?.name} {item?.guestUser?.surname}</Typography>
                <Typography variant='body2'>{item?.guestUser?.phone}</Typography>
              </Box>
            </Box>
            <Box display='flex' flexDirection="column" gap={0.5} width={1}>
              <Typography variant='body2' align="center">{translate("ticketInfo")}</Typography>
              <Box display='flex' gap={0.5} width={1}>
                <Box width={1 / 2} display="flex" flexDirection="column" justifyContent="center">
                  <Typography variant='body2'>{translate("price")}: <b>{item?.price}</b></Typography>
                  <Typography variant='body2'>{translate("feePrice")}: <b>{item?.feePrice}</b></Typography>
                </Box>
                <Box width={1 / 2} display="flex" flexDirection="column" justifyContent="center">
                  <Typography variant='body2'>{translate("cardHolder")}: <b>{item?.paymentInfo?.paymentCardHolder}</b></Typography>
                  <Typography variant='body2'>{translate("cardNumber")}: <b>{item?.paymentInfo?.formattedCardNumber}</b></Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
      <Button
        sx={{
          color: "#fc7f04",
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          lineHeight: 1,
        }}
        onClick={() => setChangeStatus((prev) => !prev)}
        startIcon={
          changeStatus
            ? <CheckBoxIcon style={{ marginRight: "10px" }} />
            : <CheckBoxOutlineBlankIcon style={{ marginRight: "10px" }} />
        }
      >
        {translate("ticketStatus")}
      </Button>
      <Button
        sx={{
          color: "#fc7f04",
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          lineHeight: 1,
        }}
        onClick={() => setReturnMoney((prev) => !prev)}
        startIcon={
          returnMoney
            ? <CheckBoxIcon style={{ marginRight: "10px" }} />
            : <CheckBoxOutlineBlankIcon style={{ marginRight: "10px" }} />
        }
      >
        {translate("returnMoney")}
      </Button>
      <Button
        sx={{
          color: "#fc7f04",
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          lineHeight: 1,
        }}
        onClick={() => setSendSms((prev) => !prev)}
        startIcon={
          sendSms
            ? <CheckBoxIcon style={{ marginRight: "10px" }} />
            : <CheckBoxOutlineBlankIcon style={{ marginRight: "10px" }} />
        }
      >
        {translate("sendSms")}
      </Button>
      <Button
        sx={{
          color: "#fc7f04",
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          lineHeight: 1,
        }}
        onClick={() => setGenerateDocs((prev) => !prev)}
        startIcon={
          generateDocs
            ? <CheckBoxIcon style={{ marginRight: "10px" }} />
            : <CheckBoxOutlineBlankIcon style={{ marginRight: "10px" }} />
        }
      >
        {translate("yeshInvoiceDocs")}
      </Button>
      <Button
        sx={{
          color: "#fc7f04",
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          lineHeight: 1,
        }}
        onClick={() => setFullReturn((prev) => !prev)}
        startIcon={
          fullReturn
            ? <CheckBoxIcon style={{ marginRight: "10px" }} />
            : <CheckBoxOutlineBlankIcon style={{ marginRight: "10px" }} />
        }
      >
        {translate("fullReturn")}
      </Button>
      <Button
        sx={{
          color: "#fc7f04",
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          lineHeight: 1,
        }}
        onClick={() => setExistDocumentSkip((prev) => !prev)}
        startIcon={
          existDocumentSkip
            ? <CheckBoxIcon style={{ marginRight: "10px" }} />
            : <CheckBoxOutlineBlankIcon style={{ marginRight: "10px" }} />
        }
      >
        {translate("existDocumentSkip")}
      </Button>
      {guestDetails?.list?.some?.((item: any) => item?.isNeedToAdditionalConfirm) ? (
        <Box padding={1} border='1px solid #d32f2f' borderRadius={2} width={1} display="flex" flexDirection='column' gap={1}>
          <Typography color='error' fontWeight='bold'>
            {intl.formatMessage({ id: 'warningCancel' })}
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={isConfirmOverdraftCancel} color='warning' />}
            label={intl.formatMessage({ id: 'warningCancelCheckbox' })}
            onChange={() => setIsConfirmOverdraftCancel((prev) => !prev)}
          />
        </Box>
      ) : null}
      <Button
        size="large"
        variant="contained"
        color="warning"
        fullWidth
        disabled={isLoading || (guestDetails?.list?.some?.((item: any) => item?.isNeedToAdditionalConfirm) && !isConfirmOverdraftCancel)}
        onClick={handleSubmit}
      >
        {translate("cancelTicketByUuidSubmit")}
      </Button>
      <Button
        size="large"
        variant="contained"
        color="warning"
        fullWidth
        onClick={handleBack}
      >
        {translate("backToSearch")}
      </Button>
    </Box>
  );

  return (
    <Modal
      title={translate("cancelTicketByUuidModalTitle")}
      visible={visible === "open"}
      style={{ maxWidth: "500px" }}
      setVisible={onCloseModal}
    >
      {view === 'search' ? searchView() : null}
      {view === 'item' ? itemView() : null}
    </Modal>
  );
}
